import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createMerchantUserAdapter } from 'deposit/adapters';
import { createSelector } from 'reselect';

import {
  CardInfo,
  CreditCard,
  DefaultPM,
  FlowTemplates,
  PaymentMethod,
  PaymentMethodStatus,
  PreviewAuthResponse,
  PreviewSummResponse,
  Purchase,
} from 'deposit/services/deposit-service';
import {
  defaultDepositMethods,
  Discount,
  MerchantDepositDetail,
  MerchantNotification,
  merchantTypes,
  Wallet,
} from 'deposit/services/types';
import { DepositState } from './types';

import { EDateFormats } from 'common/config/constants/date-formats';
import { validateExpirationDate } from 'common/helpers/date';

import { RootState } from '../store';

const initialState: DepositState = {
  paymentMethods: [],
  appPaymentMethods: [],
};

export const globalSlice = createSlice({
  name: 'deposit',
  initialState,
  reducers: {
    resetStore: (state) => {
      state = initialState;
      return state;
    },
    setDepositInfo: (state, action: PayloadAction<MerchantDepositDetail | undefined>) => {
      state.depositInfo = action.payload;
    },
    setMerchantRedirection: (state, action: PayloadAction<string>) => {
      state.merchantRedirection = action.payload;
    },
    setDefaultPM: (state, action: PayloadAction<DefaultPM | undefined>) => {
      state.defaultPM = action.payload;
    },
    setWallet: (state, action: PayloadAction<Wallet | undefined>) => {
      state.wallet = action.payload;
    },
    setPaymentMethod: (state, action: PayloadAction<PaymentMethod | undefined>) => {
      state.paymentMethod = action.payload;
    },
    setPurchase: (state, action: PayloadAction<Purchase | undefined>) => {
      state.purchaseInfo = action.payload;
    },
    setNeedsValidation: (state, action: PayloadAction<PreviewAuthResponse | undefined>) => {
      state.needsValidation = action.payload;
    },
    setPurchaseSummary: (state, action: PayloadAction<PreviewSummResponse | undefined>) => {
      state.purchaseSummary = action.payload;
    },
    setCard: (state, action: PayloadAction<CardInfo>) => {
      state.card = action.payload;
    },
    setPaymentMethods: (state, action: PayloadAction<PaymentMethod[]>) => {
      state.paymentMethods = action.payload;
    },
    setAppPaymentMethods: (state, action: PayloadAction<PaymentMethod[]>) => {
      state.appPaymentMethods = action.payload;
    },
    setCreditCards: (state, action: PayloadAction<CreditCard[]>) => {
      state.creditCards = action.payload;
    },
    setCreditCard: (state, action: PayloadAction<CreditCard | undefined>) => {
      state.creditCard = action.payload;
    },
    setDepositCustomAmount: (state, action: PayloadAction<number | undefined>) => {
      state.customAmount = action.payload;
    },
    setPromoCode: (state, action: PayloadAction<string | undefined>) => {
      state.promoCode = action.payload;
    },
  },
});

export const makeFakePaymentMethod = (name: string, code: string): PaymentMethod => ({
  id: 0,
  type: 'PM',
  code,
  name,
  image: code,
  priority: 1,
  pmDescription: '',
  description: '',
  status: {
    active: true,
    data: { type: PaymentMethodStatus.ACTIVE },
  },
  flowTemplate: FlowTemplates.DEFAULT,
  duration: '',
  subscriptionEnabled: false,
});

// Selectors
const getDepositState = (state: RootState): DepositState => state.deposit;
export const getDepositInfo = createSelector(getDepositState, (deposit) => deposit.depositInfo);
export const getDeposit = createSelector(getDepositInfo, (depositInfo) =>
  depositInfo
    ? depositInfo.deposit
    : {
        amount: 0,
        currency: '',
        status: '',
        expiration: '',
        paymentMethodCode: undefined,
        paymentMethodName: undefined,
        depositFinalAmount: 0,
        country: '',
        paymentMethodEnforced: false,
      },
);
export const getCustomLanding = createSelector(
  getDepositInfo,
  (depositInfo) => depositInfo?.customFlow?.landing,
);
export const getCustomFlow = createSelector(
  getDepositInfo,
  (depositInfo) => depositInfo?.customFlow,
);
export const getMerchant = createSelector(getDepositInfo, (depositInfo) => {
  if (depositInfo) {
    if (depositInfo.visualInfo) {
      const merchantOperation =
        depositInfo.merchant.merchantType === merchantTypes.MICROMERCHANT
          ? 'deposit_operation_payment'
          : 'deposit_operation_base';
      return {
        id: depositInfo.merchant.id,
        name: depositInfo.visualInfo.merchantName ?? depositInfo.merchant.name,
        img: depositInfo.visualInfo.merchantLogo ?? depositInfo.merchant.img,
        url: depositInfo.merchant.url,
        type: depositInfo.merchant.merchantType,
        merchantOperation,
      };
    }
    return depositInfo.merchant;
  }
  return { name: '', img: '', url: '', merchantOperation: 'deposit_operation_base' };
});

export const getMerchantRedirection = createSelector(
  getDepositState,
  (deposit) => deposit.merchantRedirection,
);

export const getMerchantUser = createSelector(getDepositInfo, (depositInfo) => {
  if (depositInfo) {
    let user;
    if (depositInfo.merchant && depositInfo.merchant.user) {
      user = depositInfo.merchant.user;
    } else if (depositInfo.user) {
      user = depositInfo.user;
    }
    return user && createMerchantUserAdapter(user);
  }
});

export const getDepositUser = createSelector(
  getDepositInfo,
  (depositInfo) =>
    (depositInfo && depositInfo?.user) ?? {
      fullName: '',
      phone: '',
      notify: false,
      validateSameUser: false,
    },
);
export const getDepositMethods = createSelector(
  getDepositInfo,
  (depositInfo) => (depositInfo && depositInfo?.depositMethods) ?? defaultDepositMethods,
);
export const getMerchantNotifications = createSelector(
  getDepositInfo,
  (depositInfo) => (depositInfo && depositInfo?.notifications) ?? ([] as MerchantNotification[]),
);
export const getDefaultPM = createSelector(getDepositState, (deposit) => deposit.defaultPM);
export const getWallet = createSelector(getDepositState, (deposit) => deposit.wallet);
export const getPaymentMethod = createSelector(getDepositState, (deposit) => deposit.paymentMethod);

export const getPaymentMethods = createSelector(
  getDepositState,
  (deposit) => deposit.paymentMethods,
);

export const getAppPaymentMethods = createSelector(
  getDepositState,
  (deposit) => deposit.appPaymentMethods,
);
export const getCard = createSelector(getDepositState, (deposit) => deposit.card);
export const getPurchase = createSelector(getDepositState, (deposit) => deposit.purchaseInfo);
export const getNeedsValidation = createSelector(
  getDepositState,
  (deposit) => deposit.needsValidation,
);
export const getPurchaseSummary = createSelector(
  getDepositState,
  (deposit) => deposit.purchaseSummary,
);

export const getCreditCards = createSelector(getDepositState, (deposit) => {
  if (deposit && deposit.creditCards) {
    const validCards = [] as CreditCard[];
    deposit.creditCards?.map((creditCard) => {
      if (validateExpirationDate(creditCard.expirationDate, EDateFormats.MM_yyyy)) {
        validCards.push(creditCard);
      }
    });

    return validCards.length ? validCards : undefined;
  }
});

export const getCreditCard = createSelector(getDepositState, (deposit) => {
  if (deposit.creditCard) {
    return deposit.creditCard;
  } else if (deposit.creditCards && deposit.creditCards.length === 1) {
    return deposit.creditCards[0];
  }
  return undefined;
});
export const getDiscounts = createSelector(getDepositState, (deposit) =>
  deposit.depositInfo ? deposit.depositInfo.discounts : ([] as Discount[]),
);
export const getBonus = createSelector(getDepositState, (deposit) => deposit.depositInfo?.bonus);

export const getCashback = createSelector(
  getDepositState,
  (deposit) => deposit.depositInfo?.cashbackBonus,
);

export const getSubscription = createSelector(
  getDepositState,
  (deposit) => deposit.depositInfo?.subscription,
);

export const getDepositCustomAmount = createSelector(
  getDepositState,
  (deposit) => deposit.customAmount,
);

export const getAstrocoinsReward = createSelector(
  getDepositState,
  (state) => state.depositInfo?.astrocoins,
);

export const getPromoCode = createSelector(getDepositState, (state) => state.promoCode);

const { actions, reducer } = globalSlice;

export const {
  resetStore: depositReset,
  setDepositInfo,
  setMerchantRedirection,
  setDefaultPM,
  setWallet,
  setPaymentMethod,
  setPaymentMethods,
  setAppPaymentMethods,
  setCard,
  setPurchase,
  setNeedsValidation,
  setPurchaseSummary,
  setCreditCards,
  setCreditCard,
  setDepositCustomAmount,
  setPromoCode,
} = actions;

export default reducer;
